/**
 * admin_database_schema_controller.js — Admin > Database > one schema (Stimulus).
 *
 * Draws the schema's ERD with Mermaid, and runs the page's table-columns,
 * rollback-preview, copy-drift-SQL and full-screen-ERD actions. Ported from an
 * inline <script> that rendered twice (its block sat inside {% block content %},
 * so the second copy died on a redeclared const) and that drew the ERD with
 * mermaid's startOnLoad, which waits for a window load event a Turbo visit
 * never fires. connect() runs mermaid itself, loading the vendor script first
 * when the page was reached by a Turbo visit.
 *
 * Values: schemaName, migrationType (sent with a rollback), mermaidSrc (the
 * vendored mermaid.min.js). Targets: driftSql (the drift SQL to copy), erd
 * (the diagram container toggled full screen).
 *
 * Template: templates/admin/database/schema_detail.html
 */

import { Controller } from "@hotwired/stimulus";
import { loadScript } from "./_load_script.js";

const FULL_SCREEN = ['position-fixed', 'top-0', 'start-0', 'w-100', 'h-100', 'bg-white', 'p-4'];

export default class extends Controller {
    static values = { schemaName: String, migrationType: String, mermaidSrc: String };
    static targets = ['driftSql', 'erd'];

    connect() {
        const diagrams = this.element.querySelectorAll('.mermaid:not([data-processed])');
        if (diagrams.length === 0) return;
        loadScript(this.mermaidSrcValue, 'mermaid').then(function (mermaid) {
            mermaid.initialize({ startOnLoad: false, theme: 'default' });
            mermaid.run({ nodes: diagrams });
        }).catch(function (error) { console.error('ERD not drawn:', error); });
    }

    showTableColumns(event) {
        const table = event.params.table;
        document.getElementById('tableName').textContent = table;
        document.getElementById('columnsLoading').style.display = 'block';
        document.getElementById('columnsContent').style.display = 'none';
        bootstrap.Modal.getOrCreateInstance(document.getElementById('columnsModal')).show();

        fetch('/admin/database/api/schema/' + event.params.schema + '/table/' + table + '/columns')
            .then(function (response) { return response.json(); })
            .then(function (data) {
                document.getElementById('columnsLoading').style.display = 'none';
                document.getElementById('columnsContent').style.display = 'block';
                const tbody = document.getElementById('columnsTable');
                tbody.innerHTML = data.columns.map(columnRow).join('');
            });
    }

    rollback(event) {
        const dryRun = event.params.dryRun;
        const action = dryRun ? 'Preview Rollback' : 'Rollback';
        if (!dryRun && !confirm('Are you sure you want to rollback migrations on ' + this.schemaNameValue + '?')) return;

        this._showResult(action + '...', '<div class="spinner-border"></div>');
        fetch('/admin/database/api/rollback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                schema: this.schemaNameValue, type: this.migrationTypeValue, versions: 1, dry_run: dryRun
            })
        })
        .then(function (response) { return response.json(); })
        .then((data) => {
            if (data.success) {
                this._showResult(action + ' ' + (dryRun ? 'Preview' : 'Complete'),
                    '<pre class="bg-light p-3 rounded">' + data.output + '</pre>');
            } else {
                this._showResult('Error', '<div class="alert alert-danger">' + (data.error || data.message) + '</div>');
            }
        });
    }

    copyDriftSql() {
        navigator.clipboard.writeText(this.driftSqlTarget.textContent).then(function () {
            alert('SQL copied to clipboard!');
        });
    }

    toggleErd() {
        const container = this.erdTarget;
        FULL_SCREEN.forEach(function (cls) { container.classList.toggle(cls); });
        container.style.zIndex = container.classList.contains('position-fixed') ? '1050' : '';
    }

    _showResult(title, content) {
        document.getElementById('resultModalTitle').textContent = title;
        document.getElementById('resultContent').innerHTML = content;
        bootstrap.Modal.getOrCreateInstance(document.getElementById('resultModal')).show();
    }
}

function columnRow(col) {
    return '<tr><td><code>' + col.name + '</code></td><td><code>' + col.type + '</code></td>'
        + '<td>' + (col.nullable ? '<i class="bi bi-check text-success"></i>' : '<i class="bi bi-x text-danger"></i>') + '</td>'
        + '<td><small>' + (col.default || '-') + '</small></td></tr>';
}
